import React, { useState } from 'react';
import { Icon } from '@iconify/react';
import type { CashflowYearlyReport } from '@/types/cashflow';
import {
  formatRupiah,
  exportYearlyReportToExcel,
  exportYearlyReportToCSV,
} from '@/utils/cashflowUtils';
import showToast from '@/components/ui/Toast';

interface CashflowYearlyTableProps {
  report: CashflowYearlyReport | null;
  selectedYear: number;
  onYearChange: (year: number) => void;
  isLoading?: boolean;
  communityName?: string;
}

export const CashflowYearlyTable: React.FC<CashflowYearlyTableProps> = ({
  report,
  selectedYear,
  onYearChange,
  isLoading,
  communityName,
}) => {
  const [isExportingExcel, setIsExportingExcel] = useState(false);
  const [isExportingCsv, setIsExportingCsv] = useState(false);

  const rows = report?.rows ?? report?.months ?? [];

  // Calculate totals if not provided directly
  const totalEvents =
    report?.total_events ??
    rows.reduce((acc, r) => acc + (r.event_count ?? 0), 0);
  const totalIncome = report?.total_income ?? 0;
  const totalExpense = report?.total_expense ?? 0;
  const totalNet =
    report?.total_net ?? report?.net_balance ?? totalIncome - totalExpense;

  const handleExportExcel = async () => {
    try {
      setIsExportingExcel(true);
      await exportYearlyReportToExcel(report, selectedYear, communityName);
      showToast.success('Laporan Excel berhasil diunduh');
    } catch (err: any) {
      console.error('Export Excel error:', err);
      showToast.error(err?.message || 'Gagal mengekspor laporan Excel');
    } finally {
      setIsExportingExcel(false);
    }
  };

  const handleExportCsv = async () => {
    try {
      setIsExportingCsv(true);
      await exportYearlyReportToCSV(report, selectedYear, communityName);
      showToast.success('Laporan CSV berhasil diunduh');
    } catch (err: any) {
      console.error('Export CSV error:', err);
      showToast.error(err?.message || 'Gagal mengekspor laporan CSV');
    } finally {
      setIsExportingCsv(false);
    }
  };

  return (
    <div className="bg-white rounded-3xl p-5 border border-[#E0E6ED] shadow-2xs space-y-4">
      {/* Header with Year Selector */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
        <div>
          <h2 className="text-base font-extrabold text-[#0E1726]">
            Laporan Kas Tahunan
          </h2>
          <p className="text-xs text-gray-400 font-medium">
            Rekap arus kas bulanan & export ke Excel / CSV
          </p>
        </div>

        <div className="flex items-center gap-1.5 self-start sm:self-auto bg-gray-50 border border-gray-200/80 rounded-2xl px-2 py-1">
          <button
            type="button"
            onClick={() => onYearChange(selectedYear - 1)}
            className="w-7 h-7 rounded-xl hover:bg-white flex items-center justify-center text-gray-600 transition-colors cursor-pointer"
            title="Tahun Sebelumnya"
          >
            <Icon icon="ph:caret-left-bold" className="w-3.5 h-3.5" />
          </button>
          <span className="text-xs font-black text-[#0E1726] px-1">
            {selectedYear}
          </span>
          <button
            type="button"
            onClick={() => onYearChange(selectedYear + 1)}
            className="w-7 h-7 rounded-xl hover:bg-white flex items-center justify-center text-gray-600 transition-colors cursor-pointer"
            title="Tahun Berikutnya"
          >
            <Icon icon="ph:caret-right-bold" className="w-3.5 h-3.5" />
          </button>
        </div>
      </div>

      {/* Annual Summary Mini Cards */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-2.5 p-3.5 bg-gray-50 rounded-2xl border border-[#E0E6ED]">
        <div>
          <span className="text-[10px] font-semibold text-gray-400 block">
            Total Event
          </span>
          <span className="text-xs sm:text-sm font-extrabold text-[#0E1726] truncate block">
            {totalEvents} Event
          </span>
        </div>
        <div>
          <span className="text-[10px] font-semibold text-gray-400 block">
            Total Pemasukan
          </span>
          <span className="text-xs sm:text-sm font-extrabold text-emerald-600 truncate block">
            {formatRupiah(totalIncome)}
          </span>
        </div>
        <div>
          <span className="text-[10px] font-semibold text-gray-400 block">
            Total Pengeluaran
          </span>
          <span className="text-xs sm:text-sm font-extrabold text-rose-600 truncate block">
            {formatRupiah(totalExpense)}
          </span>
        </div>
        <div>
          <span className="text-[10px] font-semibold text-gray-400 block">
            NET Tahunan
          </span>
          <span
            className={`text-xs sm:text-sm font-extrabold truncate block ${
              totalNet >= 0 ? 'text-blue-600' : 'text-red-600'
            }`}
          >
            {formatRupiah(totalNet)}
          </span>
        </div>
      </div>

      {/* Export Action Buttons */}
      <div className="flex flex-wrap items-center gap-2.5 pt-0.5">
        <button
          type="button"
          onClick={handleExportExcel}
          disabled={isExportingExcel || isExportingCsv || isLoading}
          className="flex-1 min-w-[140px] px-3.5 py-2.5 rounded-xl bg-emerald-600 hover:bg-emerald-700 disabled:opacity-50 text-white font-bold text-xs flex items-center justify-center gap-2 shadow-xs transition-all active:scale-[0.98] cursor-pointer"
        >
          {isExportingExcel ? (
            <Icon icon="line-md:loading-loop" className="w-4 h-4" />
          ) : (
            <Icon icon="ph:file-xls-bold" className="w-4 h-4 text-emerald-100" />
          )}
          <span>Export Excel (.xlsx)</span>
        </button>

        <button
          type="button"
          onClick={handleExportCsv}
          disabled={isExportingExcel || isExportingCsv || isLoading}
          className="flex-1 min-w-[140px] px-3.5 py-2.5 rounded-xl bg-white hover:bg-gray-50 disabled:opacity-50 text-gray-700 font-bold text-xs border border-gray-200 shadow-2xs flex items-center justify-center gap-2 transition-all active:scale-[0.98] cursor-pointer"
        >
          {isExportingCsv ? (
            <Icon icon="line-md:loading-loop" className="w-4 h-4 text-gray-600" />
          ) : (
            <Icon icon="ph:file-csv-bold" className="w-4 h-4 text-blue-600" />
          )}
          <span>Export CSV (.csv)</span>
        </button>
      </div>

      {/* Table Breakdown */}
      {isLoading ? (
        <div className="space-y-2 py-2 animate-pulse">
          <div className="h-10 bg-gray-100 rounded-xl" />
          <div className="h-10 bg-gray-100 rounded-xl" />
          <div className="h-10 bg-gray-100 rounded-xl" />
        </div>
      ) : (
        <div className="overflow-x-auto -mx-5 px-5">
          <table className="w-full text-left text-xs min-w-[420px]">
            <thead>
              <tr className="border-b border-[#E0E6ED] text-gray-400 font-bold uppercase text-[10px]">
                <th className="pb-2.5">Bulan</th>
                <th className="pb-2.5 text-center">Event</th>
                <th className="pb-2.5 text-right">Pemasukan</th>
                <th className="pb-2.5 text-right">Pengeluaran</th>
                <th className="pb-2.5 text-right">NET</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-50">
              {rows.map((m) => {
                const income = m.total_income ?? m.income ?? 0;
                const expense = m.total_expense ?? m.expense ?? 0;
                const net = m.net_balance ?? m.net ?? income - expense;
                const eventCount = m.event_count ?? 0;
                const isZero = income === 0 && expense === 0 && eventCount === 0;
                const monthTitle =
                  m.month_name || m.month_short || `Bulan ${m.month}`;
                return (
                  <tr
                    key={m.month}
                    className={`hover:bg-gray-50/80 transition-colors ${
                      isZero ? 'text-gray-400' : 'text-gray-800'
                    }`}
                  >
                    <td className="py-2.5 font-bold">{monthTitle}</td>
                    <td className="py-2.5 text-center font-medium text-gray-500">
                      {eventCount > 0 ? `${eventCount} event` : '-'}
                    </td>
                    <td className="py-2.5 text-right font-medium text-emerald-600">
                      {income > 0 ? formatRupiah(income) : '-'}
                    </td>
                    <td className="py-2.5 text-right font-medium text-rose-600">
                      {expense > 0 ? formatRupiah(expense) : '-'}
                    </td>
                    <td
                      className={`py-2.5 text-right font-extrabold ${
                        net > 0
                          ? 'text-blue-600'
                          : net < 0
                          ? 'text-rose-600'
                          : 'text-gray-400'
                      }`}
                    >
                      {net !== 0 ? formatRupiah(net) : '-'}
                    </td>
                  </tr>
                );
              })}
            </tbody>
            <tfoot className="border-t-2 border-gray-200 bg-gray-50/60 font-bold">
              <tr>
                <td className="py-2.5 font-extrabold text-[#0E1726]">TOTAL</td>
                <td className="py-2.5 text-center font-bold text-gray-700">
                  {totalEvents} event
                </td>
                <td className="py-2.5 text-right font-extrabold text-emerald-600">
                  {formatRupiah(totalIncome)}
                </td>
                <td className="py-2.5 text-right font-extrabold text-rose-600">
                  {formatRupiah(totalExpense)}
                </td>
                <td
                  className={`py-2.5 text-right font-black ${
                    totalNet >= 0 ? 'text-blue-600' : 'text-rose-600'
                  }`}
                >
                  {formatRupiah(totalNet)}
                </td>
              </tr>
            </tfoot>
          </table>
        </div>
      )}
    </div>
  );
};
